import { useState, useEffect, useCallback } from "react";
import { Plus, UserPlus, Trash2, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function AdminTeam() {
  const [perms, setPerms] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([api.get("/admin/permissions"), api.get("/admin/assistants")]);
    setPerms(p.data); setAssistants(a.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = perms.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});

  const newAssistant = () => setForm({ name: "", email: "", password: "", permissions: [] });
  const editAssistant = (a) => setForm({ id: a.id, name: a.name, email: a.email, password: "", permissions: a.permissions || [], status: a.status });
  const togglePerm = (key) => setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((k) => k !== key) : [...f.permissions, key] }));

  const save = async () => {
    try {
      if (form.id) {
        await api.patch(`/admin/assistants/${form.id}`, { name: form.name, permissions: form.permissions, ...(form.password ? { password: form.password } : {}), status: form.status });
      } else {
        if (!form.name || !form.email || !form.password) { toast.error("Name, email and password required"); return; }
        await api.post("/admin/assistants", { name: form.name, email: form.email, password: form.password, permissions: form.permissions });
      }
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (a) => { if (!window.confirm(`Remove ${a.email}?`)) return; await api.delete(`/admin/assistants/${a.id}`); toast.success("Removed"); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-fx-s3">Create staff accounts and grant only the permissions they need. Super Admin has full access.</p>
        <Button onClick={newAssistant} data-testid="team-new-btn" className="bg-brandblue hover:bg-blue-500 gap-2"><UserPlus className="h-4 w-4" /> New Assistant</Button>
      </div>
      <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Permissions</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Added</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {assistants.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-fx-s3">No assistants yet.</td></tr>}
            {assistants.map((a) => (
              <tr key={a.id} data-testid={`team-row-${a.id}`} className="border-b border-white/[0.03]">
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-fx-s3">{a.email}</td>
                <td className="px-4 py-3"><span className="text-xs text-brandblue">{(a.permissions || []).length} granted</span></td>
                <td className="px-4 py-3"><span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", a.status === "suspended" ? "text-neg bg-neg/15" : "text-pos bg-pos/15")}>{a.status || "active"}</span></td>
                <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(a.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button onClick={() => editAssistant(a)} data-testid={`team-edit-${a.id}`} size="sm" className="bg-brandblue hover:bg-blue-500 h-7 mr-2">Edit</Button>
                  <Button onClick={() => del(a)} variant="ghost" size="sm" className="text-neg h-7"><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brandblue" /> {form?.id ? "Edit Assistant" : "New Assistant"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-fx-base border-white/10" /></div>
                <div className="space-y-1"><Label className="text-xs">Email</Label><Input data-testid="team-email" value={form.email} disabled={!!form.id} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-fx-base border-white/10 disabled:opacity-60" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">{form.id ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="team-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-2">
                <Label className="text-xs">Permissions</Label>
                {Object.entries(groups).map(([g, items]) => (
                  <div key={g} className="rounded-lg bg-fx-base border border-white/5 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-fx-s3 mb-2">{g}</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {items.map((p) => (
                        <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`team-perm-${p.key}`}>
                          <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} className="accent-brandblue h-4 w-4" />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="team-save" className="bg-brandblue hover:bg-blue-500">Save Assistant</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
